import { create } from "zustand";
import { API } from "@/lib/apiConfig";

/**
 * EU Cross-Sovereign Subsidy Allocation Store (v2-v3)
 * 
 * Manages country-level subsidy allocation, plafons, over-exposure detection,
 * fraud risk scoring, ESG compliance conditions, and election-year sensitivity.
 * 
 * Integrates with Settlement Ledger for financial CO₂ and ESG credit calculations.
 */

export type EUCountry = 
  | "AT" | "BE" | "BG" | "CY" | "CZ" | "DE" | "DK" | "EE" | "ES" | "FI"
  | "FR" | "GR" | "HR" | "HU" | "IE" | "IT" | "LT" | "LU" | "LV" | "MT"
  | "NL" | "PL" | "PT" | "RO" | "SE" | "SI" | "SK";

export interface CountryAllocation {
  country: EUCountry;
  plafon: number; // Maximum subsidy amount (EUR)
  allocated: number; // Currently allocated amount (EUR)
  eligibilityScore: number; // 0-100
  overExposure: boolean; // allocated > plafon * 0.9
  fraudRiskScore: number; // 0-100
  esgCompliance: boolean;
  electionYearSensitivity: number; // 0-5 (0 = no election, 5 = election year)
  lastUpdated: number;
}

export interface SubsidyAllocationStoreState {
  // Country-level allocations
  countryAllocations: Record<EUCountry, CountryAllocation>;
  
  // Global settings
  totalEUPlafon: number; // Total EU subsidy budget
  fraudRiskThreshold: number; // Threshold for fraud risk (default: 60)
  overExposureThreshold: number; // Percentage threshold (default: 90%)
  
  // Actions
  setCountryPlafon: (country: EUCountry, plafon: number) => void;
  allocateSubsidy: (country: EUCountry, amount: number) => Promise<void>;
  setEligibilityScore: (country: EUCountry, score: number) => void;
  setFraudRiskScore: (country: EUCountry, score: number) => void;
  setESGCompliance: (country: EUCountry, compliant: boolean) => void;
  setElectionYearSensitivity: (country: EUCountry, sensitivity: number) => void;
  
  // Computed values
  getOverExposedCountries: () => EUCountry[];
  getHighFraudRiskCountries: () => EUCountry[];
  getTotalAllocated: () => number;
  getRemainingBudget: () => number;
  getCountryUtilization: (country: EUCountry) => number; // percentage
  
  // Export
  exportAllocationReport: () => void;
}

const DEFAULT_PLAFONS: Record<EUCountry, number> = {
  AT: 50000000, BE: 60000000, BG: 80000000, CY: 20000000, CZ: 70000000,
  DE: 200000000, DK: 40000000, EE: 30000000, ES: 150000000, FI: 50000000,
  FR: 180000000, GR: 90000000, HR: 40000000, HU: 60000000, IE: 30000000,
  IT: 170000000, LT: 35000000, LU: 15000000, LV: 30000000, MT: 10000000,
  NL: 80000000, PL: 120000000, PT: 70000000, RO: 80000000, SE: 60000000,
  SI: 25000000, SK: 40000000,
};

function initializeCountryAllocations(): Record<EUCountry, CountryAllocation> {
  const allocations: Record<EUCountry, CountryAllocation> = {} as any;
  
  (Object.keys(DEFAULT_PLAFONS) as EUCountry[]).forEach((country) => {
    allocations[country] = {
      country,
      plafon: DEFAULT_PLAFONS[country],
      allocated: 0,
      eligibilityScore: 100,
      overExposure: false,
      fraudRiskScore: 0,
      esgCompliance: true,
      electionYearSensitivity: 0,
      lastUpdated: Date.now(),
    };
  });
  
  return allocations;
}

const API_BASE = API.subsidyAllocation;

export const useSubsidyAllocationStore = create<SubsidyAllocationStoreState>((set, get) => ({
  countryAllocations: initializeCountryAllocations(),
  totalEUPlafon: Object.values(DEFAULT_PLAFONS).reduce((a, b) => a + b, 0),
  fraudRiskThreshold: 60,
  overExposureThreshold: 90,

  setCountryPlafon: (country, plafon) => {
    set((state) => {
      const allocation = { ...state.countryAllocations[country] };
      allocation.plafon = plafon;
      allocation.overExposure = allocation.allocated > (plafon * state.overExposureThreshold / 100);
      allocation.lastUpdated = Date.now();
      
      return {
        countryAllocations: {
          ...state.countryAllocations,
          [country]: allocation,
        },
      };
    });
  },

  allocateSubsidy: async (country, amount) => {
    // Try to sync with backend if available
    try {
      const response = await fetch(`${API_BASE}/allocations/${country}/allocate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount }),
      });
      if (response.ok) {
        const data = await response.json();
        // Update local state from backend response
        set((state) => ({
          countryAllocations: {
            ...state.countryAllocations,
            [country]: data.allocation,
          },
        }));
        return;
      }
    } catch (error) {
      console.log("Backend not available, using local state only");
    }
    
    // Fallback to local state
    set((state) => {
      const allocation = { ...state.countryAllocations[country] };
      allocation.allocated = Math.max(0, allocation.allocated + amount);
      allocation.overExposure = allocation.allocated > (allocation.plafon * state.overExposureThreshold / 100);
      allocation.lastUpdated = Date.now();
      
      return {
        countryAllocations: {
          ...state.countryAllocations,
          [country]: allocation,
        },
      };
    });
  },

  setEligibilityScore: (country, score) => {
    set((state) => {
      const allocation = { ...state.countryAllocations[country] };
      allocation.eligibilityScore = Math.max(0, Math.min(100, score));
      allocation.lastUpdated = Date.now();
      
      return {
        countryAllocations: {
          ...state.countryAllocations,
          [country]: allocation,
        },
      };
    });
  },

  setFraudRiskScore: (country, score) => {
    set((state) => {
      const allocation = { ...state.countryAllocations[country] };
      allocation.fraudRiskScore = Math.max(0, Math.min(100, score));
      allocation.lastUpdated = Date.now();
      
      return {
        countryAllocations: {
          ...state.countryAllocations,
          [country]: allocation,
        },
      };
    });
  },

  setESGCompliance: (country, compliant) => {
    set((state) => {
      const allocation = { ...state.countryAllocations[country] };
      allocation.esgCompliance = compliant;
      allocation.lastUpdated = Date.now();
      
      return {
        countryAllocations: {
          ...state.countryAllocations,
          [country]: allocation,
        },
      };
    });
  },

  setElectionYearSensitivity: (country, sensitivity) => {
    set((state) => {
      const allocation = { ...state.countryAllocations[country] };
      allocation.electionYearSensitivity = Math.max(0, Math.min(5, sensitivity));
      allocation.lastUpdated = Date.now();
      
      return {
        countryAllocations: {
          ...state.countryAllocations,
          [country]: allocation,
        },
      };
    });
  },

  getOverExposedCountries: () => {
    const state = get();
    return (Object.keys(state.countryAllocations) as EUCountry[]).filter(
      (country) => state.countryAllocations[country].overExposure
    );
  },

  getHighFraudRiskCountries: () => {
    const state = get();
    return (Object.keys(state.countryAllocations) as EUCountry[]).filter(
      (country) => state.countryAllocations[country].fraudRiskScore >= state.fraudRiskThreshold
    );
  },

  getTotalAllocated: () => {
    const state = get();
    return Object.values(state.countryAllocations).reduce(
      (sum, alloc) => sum + alloc.allocated,
      0
    );
  },

  getRemainingBudget: () => {
    const state = get();
    return state.totalEUPlafon - state.getTotalAllocated();
  },

  getCountryUtilization: (country) => {
    const state = get();
    const allocation = state.countryAllocations[country];
    if (allocation.plafon === 0) return 0;
    return (allocation.allocated / allocation.plafon) * 100;
  },

  exportAllocationReport: () => {
    const state = get();
    const data = {
      version: "2.0",
      exportedAt: new Date().toISOString(),
      totalEUPlafon: state.totalEUPlafon,
      totalAllocated: state.getTotalAllocated(),
      remainingBudget: state.getRemainingBudget(),
      overExposedCountries: state.getOverExposedCountries(),
      highFraudRiskCountries: state.getHighFraudRiskCountries(),
      countryAllocations: state.countryAllocations,
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 10);
    a.download = `subsidy-allocation-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },
}));

